import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Fade, Snackbar, Typography } from '@mui/material';
import type { SnackbarCloseReason } from '@mui/material';
import { keyframes, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React, { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals, SemanticError } from '../../../../core-components/theme/color-palette/colors';
import { Button } from '../../../../core-components/theme/components/Buttons';
import { visuallyHiddenSx } from '../../../../core-components/theme/components/Fields/fieldStyles';
import { bannerAnchorSx, bannerVisibleMs, IN_APP_CARD_MAX, severityAccent } from '../../constants/inApp';
import type { AlertPhase, OpenTarget } from '../../hooks/logic/useInAppAlerts';
import { useLocale } from '../../hooks/logic/useLocale';
import type { FeedItem, Severity } from '../../types/domain.types';
import { alertLines } from '../shared/alertLines';
import { SeverityChip } from '../shared/SeverityChip';

import { moreAlertsText, startFailedText, takenText } from './inAppText';
import { useSwipeDismiss } from './useSwipeDismiss';
import { sourceText } from './whenText';

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';
const TIMER = '[data-banner-timer]';

const drain = keyframes`
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
`;

const cardSxOf =
  (severity: Severity) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    inlineSize: `min(100%, ${IN_APP_CARD_MAX}px)`,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${Neutrals[200]}`,
    borderInlineStart: `${theme.spacing(1)} solid ${severityAccent(severity)}`,
    borderRadius: 2.5,
    boxShadow: theme.customShadows.dialog,
    touchAction: 'pan-y',
    [theme.breakpoints.up('md')]: {
      inlineSize: `${IN_APP_CARD_MAX}px`,
      maxInlineSize: `calc(100vw - 2 * ${theme.spacing(6)})`,
    },
    [`&:hover ${TIMER}, &:focus-within ${TIMER}`]: { animation: 'none' },
    [REDUCED_MOTION]: { transition: 'none !important' },
  });

const CARD_SX: Readonly<Record<Severity, ReturnType<typeof cardSxOf>>> = {
  EMERGENCY: cardSxOf('EMERGENCY'),
  MAJOR: cardSxOf('MAJOR'),
  MINOR: cardSxOf('MINOR'),
  CAUTION: cardSxOf('CAUTION'),
};

const bodySx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 2,
  inlineSize: '100%',
  padding: 3,
  textAlign: 'start',
};

const sourceSx: SystemStyleObject<Theme> = { color: Neutrals[600] };
const titleRowSx: SystemStyleObject<Theme> = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 1 };
const titleSx: SystemStyleObject<Theme> = { color: Neutrals[900], overflowWrap: 'anywhere' };
const metaSx: SystemStyleObject<Theme> = { color: Neutrals[700], fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' };
const failedSx: SystemStyleObject<Theme> = { color: SemanticError[600], paddingInline: 3 };

const openSx: SystemStyleObject<Theme> = {
  alignSelf: 'flex-start',
  marginInlineStart: -2,
  '&::after': { content: '""', position: 'absolute', inset: 0 },
};

const actionsSx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 2,
  rowGap: 1,
  paddingInline: 3,
  paddingBlockEnd: 3,
  '& > *': { position: 'relative' },
};
const moreSx: SystemStyleObject<Theme> = { marginInlineStart: 'auto' };

const timerSxOf =
  (severity: Severity, ms: number) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    position: 'absolute',
    insetInline: 0,
    insetBlockEnd: 0,
    blockSize: theme.spacing(1),
    backgroundColor: severityAccent(severity),
    transformOrigin: 'left center',
    '[dir="rtl"] &': { transformOrigin: 'right center' },
    pointerEvents: 'none',
    animation: `${drain} ${ms}ms linear forwards`,
    [REDUCED_MOTION]: { display: 'none' },
  });

const OPEN_ICON: Readonly<Record<Exclude<OpenTarget, null>, React.ReactElement>> = {
  inApp: <ChevronRightIcon aria-hidden />,
  external: <OpenInNewIcon aria-hidden />,
};

interface OpenAlertButtonProps {
  readonly target: OpenTarget;
  readonly describedBy: string;
  readonly onOpen: () => void;
}

const NewTabNote: React.FC<{ target: OpenTarget }> = ({ target }) => {
  const { t } = useTranslation();
  if (target !== 'external') return null;
  return (
    <Box component="span" sx={visuallyHiddenSx}>
      {t('common.opensInNewTab', '(opens in a new tab)')}
    </Box>
  );
};

const OpenAlertButton: React.FC<OpenAlertButtonProps> = ({ target, describedBy, onOpen }) => {
  const { t } = useTranslation();
  if (target === null) return null;
  return (
    <Button intent="tertiary" endIcon={OPEN_ICON[target]} aria-describedby={describedBy} onClick={onOpen} sx={openSx}>
      {t('closeTheLoop.inApp.open', 'Open alert')}
      <NewTabNote target={target} />
    </Button>
  );
};

interface BannerBodyProps {
  readonly item: FeedItem;
  readonly now: number;
  readonly phase: AlertPhase;
  readonly openTarget: OpenTarget;
  readonly onOpen: () => void;
}

const BannerBody: React.FC<BannerBodyProps> = ({ item, now, phase, openTarget, onOpen }) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const titleId = useId();
  const lines = alertLines(t, item, locale);
  return (
    <Box sx={bodySx}>
      <Typography component="p" variant="caption2" sx={sourceSx}>
        {sourceText(t, item.at, now)}
      </Typography>
      <Box sx={titleRowSx}>
        <SeverityChip severity={item.severity} />
        <Typography id={titleId} component="p" variant="cardTitleSm" sx={titleSx}>
          {lines.title}
        </Typography>
      </Box>
      <Typography component="p" variant="caption2" sx={metaSx}>
        {phase.kind === 'taken' ? takenText(t, phase.by) : lines.meta}
      </Typography>
      <OpenAlertButton target={openTarget} describedBy={titleId} onOpen={onOpen} />
    </Box>
  );
};

const StartButton: React.FC<{ visible: boolean; starting: boolean; onStart: () => void }> = ({ visible, starting, onStart }) => {
  const { t } = useTranslation();
  if (!visible) return null;
  return (
    <Button intent="primary" busy={starting} onClick={onStart}>
      {t('closeTheLoop.inApp.start', 'Start')}
    </Button>
  );
};

const SeeAllButton: React.FC<{ pendingCount: number; onSeeAll: () => void }> = ({ pendingCount, onSeeAll }) => {
  const { t } = useTranslation();
  const label = pendingCount > 0 ? moreAlertsText(t, pendingCount) : t('closeTheLoop.inApp.seeAll', 'See all alerts');
  return (
    <Button intent="tertiary" size="small" onClick={onSeeAll} sx={moreSx}>
      {label}
    </Button>
  );
};

const useFocusReturn = (cardRef: React.RefObject<HTMLElement>) => {
  const returnTo = useRef<HTMLElement | null>(null);
  return {
    remember: (event: React.FocusEvent<HTMLElement>): void => {
      const from = event.relatedTarget;
      if (returnTo.current === null && from instanceof HTMLElement && !event.currentTarget.contains(from)) returnTo.current = from;
    },
    restore: (): void => {
      const target = returnTo.current;
      const focusInside = cardRef.current?.contains(document.activeElement) ?? false;
      if (target !== null && target.isConnected && focusInside) target.focus();
    },
  };
};

export interface InAppBannerProps {
  readonly item: FeedItem;
  readonly now: number;
  readonly phase: AlertPhase;
  readonly openTarget: OpenTarget;
  readonly pendingCount: number;
  readonly canStart: boolean;
  readonly startFailed: boolean;
  readonly onStart: () => void;
  readonly onOpen: () => void;
  readonly onDismiss: () => void;
  readonly onTimeout: () => void;
  readonly onSeeAll: () => void;
  readonly onExited: () => void;
}

export const InAppBanner: React.FC<InAppBannerProps> = ({
  item,
  now,
  phase,
  openTarget,
  pendingCount,
  canStart,
  startFailed,
  onStart,
  onOpen,
  onDismiss,
  onTimeout,
  onSeeAll,
  onExited,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);
  const focus = useFocusReturn(cardRef);
  const open = phase.kind !== 'closing';
  const visibleMs = bannerVisibleMs(item.severity);
  const swipe = useSwipeDismiss(
    onDismiss,
    phase.kind === 'live' || phase.kind === 'taken',
    theme.transitions.create('transform', { duration: theme.transitions.duration.shorter }),
  );
  const onClose = (_event: React.SyntheticEvent | Event, reason: SnackbarCloseReason) => {
    if (reason === 'timeout') onTimeout();
  };
  const exited = (): void => {
    focus.restore();
    onExited();
  };
  return (
    <Snackbar
      open={open}
      autoHideDuration={visibleMs}
      resumeHideDuration={visibleMs ?? undefined}
      disableWindowBlurListener
      onClose={onClose}
      TransitionComponent={Fade}
      TransitionProps={{ onExited: exited }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={bannerAnchorSx}
    >
      <Box
        ref={cardRef}
        role="region"
        aria-label={t('closeTheLoop.inApp.bannerLabel', 'New alert')}
        sx={CARD_SX[item.severity]}
        onFocus={focus.remember}
        {...swipe}
      >
        <BannerBody item={item} now={now} phase={phase} openTarget={openTarget} onOpen={onOpen} />
        {startFailed && (
          <Typography role="alert" component="p" variant="caption2" sx={failedSx}>
            {startFailedText(t)}
          </Typography>
        )}
        <Box sx={actionsSx}>
          <StartButton visible={canStart} starting={phase.kind === 'starting'} onStart={onStart} />
          <Button intent="tertiary" onClick={onDismiss}>
            {t('closeTheLoop.inApp.dismiss', 'Dismiss')}
          </Button>
          <SeeAllButton pendingCount={pendingCount} onSeeAll={onSeeAll} />
        </Box>
        {visibleMs !== null && <Box data-banner-timer="" aria-hidden sx={timerSxOf(item.severity, visibleMs)} />}
      </Box>
    </Snackbar>
  );
};
